import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import {
  FaUserEdit,
  FaEnvelope,
  FaLock,
  FaEye,
  FaEyeSlash,
  FaEdit,
} from "react-icons/fa";
import configuration from "../../../Config/configuration";
import { authorizedPut } from "../../../Config/networkWithToken";
import {
  config,
  errorNotify,
  successNotify,
  getValueFromLocalStorage,
  localStorageKey,
} from "../../../utils/helper";
import { useNavigate } from "react-router-dom";

const AdminUpdate = () => {
  const [isButtonLoad, setIsButtonLoad] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const navigate = useNavigate();

  // Get admin details
  const adminId = getValueFromLocalStorage(localStorageKey.USERDATA, "adminId");
  const adminName = getValueFromLocalStorage(localStorageKey.USERDATA, "name");
  const adminEmail = getValueFromLocalStorage(
    localStorageKey.USERDATA,
    "email",
  );

  // --- PROFILE FORM VALIDATION ---
  const validate = (values) => {
    const errors = {};
    if (!values.name.trim()) errors.name = "Name is required";
    if (!values.email.trim()) errors.email = "Email is required";
    else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email))
      errors.email = "Invalid email address";
    return errors;
  };

  const formik = useFormik({
    initialValues: { name: "", email: "" },
    validate,
    onSubmit: (values) => handleSubmit(values),
    enableReinitialize: true,
  });

  useEffect(() => {
    if (adminName && adminEmail) {
      formik.setValues({ name: adminName, email: adminEmail });
    }
  }, [adminName, adminEmail]);

  // --- PROFILE UPDATE HANDLER ---
  const handleSubmit = async (values) => {
    const data = { adminId, name: values.name, email: values.email };
    try {
      setIsButtonLoad(true);
      const res = await authorizedPut(
        configuration.apis.adminUpdate,
        data,
        config,
      );
      if (res?.data?.status) {
        successNotify("Profile updated successfully. Please login again.");
        localStorage.clear();
        setTimeout(() => navigate("/login"), 1500);
      } else {
        errorNotify(res?.data?.message || "Failed to update admin");
      }
    } catch (err) {
      errorNotify("Unexpected error, please try again");
    } finally {
      setIsButtonLoad(false);
    }
  };

  // --- PASSWORD UPDATE STATE ---
  const [passwordValues, setPasswordValues] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [passwordErrors, setPasswordErrors] = useState({});

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordValues((prev) => ({ ...prev, [name]: value }));
    setPasswordErrors((prev) => ({ ...prev, [name]: "" }));
  };

  // --- VALIDATE PASSWORDS ---
  const validatePasswords = () => {
    const errors = {};
    const { currentPassword, newPassword, confirmPassword } = passwordValues;

    if (!currentPassword.trim())
      errors.currentPassword = "Current password is required";
    if (!newPassword.trim()) errors.newPassword = "New password is required";
    else if (newPassword.length < 6)
      errors.newPassword = "Password must be at least 6 characters";
    else if (newPassword === currentPassword)
      errors.newPassword =
        "New password must be different from current password";

    if (!confirmPassword.trim())
      errors.confirmPassword = "Please confirm your new password";
    else if (confirmPassword !== newPassword)
      errors.confirmPassword = "Passwords do not match";

    setPasswordErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // --- UPDATE PASSWORD HANDLER ---
  const handleUpdatePassword = async () => {
    if (!validatePasswords()) return;

    const payload = {
      adminId,
      currentPassword: passwordValues.currentPassword,
      newPassword: passwordValues.newPassword,
    };

    try {
      setIsButtonLoad(true);
      const res = await authorizedPut(
        configuration.apis.adminPasswordUpdate,
        payload,
        config,
      );
      if (res?.data?.status) {
        successNotify("Password updated successfully!");
        setPasswordValues({
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        });
        setPasswordErrors({});
        setShowChangePassword(false);
      } else {
        errorNotify(res?.data?.message || "Failed to update password");
      }
    } catch (err) {
      errorNotify("Unexpected error, please try again");
    } finally {
      setIsButtonLoad(false);
    }
  };

  return (
    <div className="font-Poppins mx-auto w-full p-8">
      <div className="mx-auto w-[100%] rounded-2xl border-t-4 border-textYellow bg-white p-8 shadow-lg md:w-[85%]">
        <div className="mb-6 flex items-center">
          <h1 className="text-xl font-bold text-gray-800">
            Update - {adminName}
          </h1>
        </div>

        {/* --- ADMIN INFO FORM --- */}
        <form onSubmit={formik.handleSubmit} className="space-y-5">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700">
              <FaUserEdit className="mr-2 inline text-textYellow" /> Name
            </label>
            <input
              type="text"
              name="name"
              className="mt-1 w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-textYellow"
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.touched.name && formik.errors.name && (
              <p className="text-sm text-red-500">{formik.errors.name}</p>
            )}
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700">
              <FaEnvelope className="mr-2 inline text-textYellow" /> Email
            </label>
            <input
              type="email"
              name="email"
              className="mt-1 w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-textYellow"
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.touched.email && formik.errors.email && (
              <p className="text-sm text-red-500">{formik.errors.email}</p>
            )}
          </div>

          {/* Update Button */}
          <button
            type="submit"
            disabled={isButtonLoad}
            className="w-full rounded-lg bg-textYellow py-2 font-semibold text-white shadow transition hover:bg-[#e39a32] disabled:opacity-50 md:w-[30%]"
          >
            {isButtonLoad ? "Updating..." : "Update Admin"}
          </button>

          {/* --- PASSWORD SECTION --- */}
          <div className="border-t pt-3">
            <div
              className="flex cursor-pointer items-center justify-between"
              onClick={() => setShowChangePassword(!showChangePassword)}
            >
              <div className="flex items-center gap-2">
                <FaLock className="text-textYellow" />
                <span className="font-medium text-gray-700">
                  Change Password
                </span>
              </div>
              <FaEdit className="text-gray-500 transition hover:text-textYellow" />
            </div>

            {showChangePassword && (
              <div className="animate-fadeIn mt-4 space-y-4">
                {/* Current Password */}
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    Current Password
                  </label>
                  <div className="relative">
                    <input
                      type={showCurrent ? "text" : "password"}
                      name="currentPassword"
                      placeholder="Enter current password"
                      className="w-full rounded-lg border px-3 py-2 text-[12px] focus:outline-none focus:ring-2 focus:ring-textYellow"
                      value={passwordValues.currentPassword}
                      onChange={handlePasswordChange}
                    />
                    <span
                      className="absolute right-3 top-2.5 cursor-pointer text-gray-500"
                      onClick={() => setShowCurrent(!showCurrent)}
                    >
                      {showCurrent ? <FaEye /> : <FaEyeSlash />}
                    </span>
                  </div>
                  {passwordErrors.currentPassword && (
                    <p className="text-sm text-red-500">
                      {passwordErrors.currentPassword}
                    </p>
                  )}
                </div>

                {/* New Password */}
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    New Password
                  </label>
                  <div className="relative">
                    <input
                      type={showNew ? "text" : "password"}
                      name="newPassword"
                      placeholder="Enter new password"
                      className="w-full rounded-lg border px-3 py-2 text-[12px] focus:outline-none focus:ring-2 focus:ring-textYellow"
                      value={passwordValues.newPassword}
                      onChange={handlePasswordChange}
                    />
                    <span
                      className="absolute right-3 top-2.5 cursor-pointer text-gray-500"
                      onClick={() => setShowNew(!showNew)}
                    >
                      {showNew ? <FaEye /> : <FaEyeSlash />}
                    </span>
                  </div>
                  {passwordErrors.newPassword && (
                    <p className="text-sm text-red-500">
                      {passwordErrors.newPassword}
                    </p>
                  )}
                </div>

                {/* Confirm Password */}
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    Confirm New Password
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirm ? "text" : "password"}
                      name="confirmPassword"
                      placeholder="Confirm new password"
                      className="w-full rounded-lg border px-3 py-2 text-[12px] focus:outline-none focus:ring-2 focus:ring-textYellow"
                      value={passwordValues.confirmPassword}
                      onChange={handlePasswordChange}
                    />
                    <span
                      className="absolute right-3 top-2.5 cursor-pointer text-gray-500"
                      onClick={() => setShowConfirm(!showConfirm)}
                    >
                      {showConfirm ? <FaEye /> : <FaEyeSlash />}
                    </span>
                  </div>
                  {passwordErrors.confirmPassword && (
                    <p className="text-sm text-red-500">
                      {passwordErrors.confirmPassword}
                    </p>
                  )}
                </div>

                {/* Update Password Button */}
                <button
                  type="button"
                  disabled={isButtonLoad}
                  onClick={handleUpdatePassword}
                  className="w-full rounded-lg bg-textYellow py-2 font-semibold text-white shadow hover:bg-[#e39a32] disabled:opacity-50 md:w-[30%]"
                >
                  {isButtonLoad ? "Updating..." : "Update Password"}
                </button>
              </div>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminUpdate;
